/**
 * Per-type *Entity virtual views: the entity registry (`Entity_Agent`)
 * split by `entityType` into one agent-facing view per hierarchy level, each
 * enriched with the distinct-descendant counts and/or ancestry that level
 * cares about.
 *
 * `Entity_Agent` is one flat registry of every hierarchy node — companies,
 * assets, fields, reservoirs, locations, wells and producing strings — tagged
 * by `entityType`. Browsing it raw forces the agent to filter by type and to
 * hand-roll descendant counts off `FlatHierarchy_Agent`. These views front it:
 * each fixes `where entityType = <type>`, keeps identity + display name, and
 * appends the per-level rollups:
 *
 *   - FieldEntity     → reservoirCount, wellCount, stringCount
 *   - ReservoirEntity → wellCount, stringCount   (sectorCount deferred — see below)
 *   - AssetEntity     → fieldCount, wellCount, stringCount
 *   - CompanyEntity   → assetCount
 *   - LocationEntity  → wellCount
 *   - StringEntity    → well / reservoir / field ANCESTRY structs
 *   - WellEntity      → reservoir / field ANCESTRY structs
 *
 * Every count is computed in `fetch` (no `countDistinct` op exists) via the
 * shared `countDescendants` helper: a single `FlatHierarchy_Agent` read filtered
 * by the level's relation column (`field` / `reservoir` / `asset` / …) over the
 * entity rows' own identities, then distinct descendant refs per relation,
 * aligned 1:1 (`0` where a node has no descendants). StringEntity's ancestry is
 * the entity-grain analog of the Hierarchy view: the producing-string ref is
 * the entity row's OWN identity, resolved through `enrichWithAncestors`.
 *
 * Both ride the ONE enriched factory (`createEnrichedView`, extended with an
 * optional `counts` spec and a `'self'` ref source) rather than a parallel
 * factory — the FlatHierarchy read seam, the de-prefix/typing SSOT
 * (`resolveColumnSet`) and the `ctx.session.fetch` memoization are shared
 * verbatim. WellEntity carries no rollup but resolves its reservoir / field
 * ancestry off its OWN well identity (`from: 'self'`, `selfColumn: 'well'`) —
 * the well-grain analog of StringEntity's string-keyed enrichment; all per-type
 * views share the same source + `where` skeleton + `ENTITY_KEEP` shape (the SSOT
 * for the entity passthrough columns).
 *
 * sectorCount for ReservoirEntity is DEFERRED: `sector` lives only on
 * `SectorHierarchy_Agent`, not `FlatHierarchy_Agent`, and `countDescendants` is
 * hardwired to the flat hierarchy. Counting sectors needs either a second read
 * against the sector view or a generalization of the count helper to take the
 * source view as a parameter — tracked as a follow-up rather than special-cased
 * here.
 *
 * Built per session (not module-level) because the enriched views close over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve rollups.
 */

import { eq } from '../ast.ts';
import { type CountSpec, createEnrichedView } from '../createEnrichedView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import type { AncestorRelation } from '../enrichAncestors.ts';
import { IDENTITY, type Shape } from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { EntityAgentView } from '#schema/generated/ipso/views.ts';

/**
 * The entity passthrough every per-type view keeps: instance identity plus the
 * display name. The SSOT for the entity-registry kept columns; the `name`
 * description is per-level so the agent reads the right noun.
 */
const entityKeep = (label: string): Shape => [
  ...IDENTITY,
  { canonical: 'name', suffix: 'name', description: `Display name of the ${label}.`, kind: 'unitless' },
];

/**
 * One count-bearing per-type entity view spec: the `entityType` it fronts, the
 * FlatHierarchy relation column its own identity is matched on, and the distinct
 * descendant counts to roll up. `entityType` literals are the lowercase
 * singulars mirroring the existing WellEntity (`'well'`).
 */
type EntityCountSpec = ViewFamilySpec & {
  readonly label: string;
  readonly entityType: string;
  readonly parentColumn: AncestorRelation;
  readonly counts: readonly CountSpec[];
};

const ENTITY_COUNT_SPECS: readonly EntityCountSpec[] = [
  {
    name: 'FieldEntity',
    description:
      'Fields (the field-grain subset of the entity registry) with display name, identity, and rolled-up counts of the distinct reservoirs, wells and producing strings beneath each field.',
    label: 'field',
    entityType: 'field',
    parentColumn: 'field',
    counts: [
      {
        column: 'reservoirCount',
        descendantRelation: 'reservoir',
        description: 'Distinct reservoirs under this field.',
      },
      { column: 'wellCount', descendantRelation: 'well', description: 'Distinct wells under this field.' },
      {
        column: 'stringCount',
        descendantRelation: 'string',
        description: 'Distinct producing strings under this field.',
      },
    ],
  },
  {
    name: 'ReservoirEntity',
    description:
      'Reservoirs (the reservoir-grain subset of the entity registry) with display name, identity, and rolled-up counts of the distinct wells and producing strings beneath each reservoir.',
    label: 'reservoir',
    entityType: 'reservoir',
    parentColumn: 'reservoir',
    counts: [
      { column: 'wellCount', descendantRelation: 'well', description: 'Distinct wells under this reservoir.' },
      {
        column: 'stringCount',
        descendantRelation: 'string',
        description: 'Distinct producing strings under this reservoir.',
      },
    ],
  },
  {
    name: 'AssetEntity',
    description:
      'Assets (the asset-grain subset of the entity registry) with display name, identity, and rolled-up counts of the distinct fields, wells and producing strings beneath each asset.',
    label: 'asset',
    entityType: 'asset',
    parentColumn: 'asset',
    counts: [
      { column: 'fieldCount', descendantRelation: 'field', description: 'Distinct fields under this asset.' },
      { column: 'wellCount', descendantRelation: 'well', description: 'Distinct wells under this asset.' },
      {
        column: 'stringCount',
        descendantRelation: 'string',
        description: 'Distinct producing strings under this asset.',
      },
    ],
  },
  {
    name: 'CompanyEntity',
    description:
      'Companies (the company-grain subset of the entity registry) with display name, identity, and a rolled-up count of the distinct assets beneath each company.',
    label: 'company',
    entityType: 'company',
    parentColumn: 'company',
    counts: [{ column: 'assetCount', descendantRelation: 'asset', description: 'Distinct assets under this company.' }],
  },
  {
    name: 'LocationEntity',
    description:
      'Locations (the location-grain subset of the entity registry) with display name, identity, and a rolled-up count of the distinct wells at each location.',
    label: 'location',
    entityType: 'location',
    parentColumn: 'location',
    counts: [{ column: 'wellCount', descendantRelation: 'well', description: 'Distinct wells at this location.' }],
  },
];

/** The ancestry relations StringEntity surfaces for each producing string. */
const STRING_ANCESTRY: readonly AncestorRelation[] = ['well', 'reservoir', 'field'];
/** The ancestry relations WellEntity surfaces for each physical wellbore. */
const WELL_ANCESTRY: readonly AncestorRelation[] = ['reservoir', 'field'];

export const useEntityViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return [
    ...ENTITY_COUNT_SPECS.map((spec: EntityCountSpec) =>
      createEnrichedView({
        client,
        mintSeries,
        name: spec.name,
        description: spec.description,
        source: EntityAgentView,
        where: eq('entityType', spec.entityType),
        keep: entityKeep(spec.label),
        counts: { parentColumn: spec.parentColumn, from: 'self', counts: spec.counts },
      }),
    ),
    createEnrichedView({
      client,
      mintSeries,
      name: 'StringEntity',
      description:
        'Producing strings (completions — the string-grain subset of the entity registry) with display name, identity, and their well / reservoir / field ancestry resolved from the production hierarchy.',
      source: EntityAgentView,
      where: eq('entityType', 'string'),
      keep: entityKeep('producing string'),
      enrich: { from: 'self', relations: STRING_ANCESTRY },
    }),
    createEnrichedView({
      client,
      mintSeries,
      name: 'WellEntity',
      description:
        'Wells (physical wellbores — the well-grain subset of the entity registry) with display name, identity, and their reservoir / field ancestry resolved from the production hierarchy.',
      source: EntityAgentView,
      where: eq('entityType', 'well'),
      keep: entityKeep('well'),
      enrich: { from: 'self', selfColumn: 'well', relations: WELL_ANCESTRY },
    }),
  ];
};
